import React, { useEffect, useState } from 'react';
import Select from 'react-select';
import { useTranslation } from 'react-i18next';

import Loading from '@components/Shared/Loader/Loading';
import { Card, CardFooter, CardHeader } from '@components/card/card';

import { useAppDispatch, useAppSelector } from '@store/hooks';
import { initReport, getReportPagedData, selectReport, setReportRequest } from '@store/DynamicSlice/reportTableSlice';

import ReportExportExcelModal, { type ExportColumn } from '@Helper/ReportPaged/ReportExportExcelModal';
import ReportPagination from '@Helper/ReportPaged/ReportPagination';

import useGetOperatingUnitWithPermission from '@hooks/NTX/Organization/useGetOperatingUnitWithPermission';
import useGlChartLookups from '@hooks/Finance/ChartAccountsDetails/useGlChartLookups';
// TODO: قم باستيراد الهوكات الخاصة بالحسابات ومراكز التكلفة إذا كانت متوفرة
// import useGetAccountsList from '@hooks/NTX/GL/useGetAccountsList';
// import useGetCostCenters from '@hooks/NTX/GL/useGetCostCenters';

/** =========================
 * REQUEST SHAPE
 * ========================= */
type GlAccountStatementReq = {
  pageIndex?: number;
  pageSize?: number;
  froM_DATE?: string | null;
  tO_DATE?: string | null;
  accounT_ID?: string | null;
  brancH_ID?: string | null;
  cosT_CENTER_ID?: string | null;
};

/** =========================
 * RESPONSE SHAPE (Array items)
 * ========================= */
type RowDto = {
  gL_DATE: string | null;
  headeR_NAME: string | null;
  jE_LINE_DESCRIPTION: string | null;
  amounT_DR: number;
  amounT_CR: number;
  cumulativE_BALANCE: number;
  accounT_NAME: string | null;
  accounT_NUMBER: string | null;
  brancH_NAME: string | null;
  cosT_CENTER_NAME: string | null;
};

const REPORT_KEY = 'RPT_GlAccountStatement';
const ENDPOINT = 'FinReports/GetGlAccountStatement';

const safe = (v: any, fallback = '—') => (v === null || v === undefined || v === '' ? fallback : String(v));

const formatNumber = (num: number | null | undefined, digits: number = 2) => {
  if (num === null || num === undefined) return '0.00';
  return new Intl.NumberFormat('en-US', { minimumFractionDigits: digits, maximumFractionDigits: digits }).format(
    num || 0
  );
};

const formatDate = (dateString: string | null) => {
  if (!dateString) return '—';
  const d = new Date(dateString);
  return new Intl.DateTimeFormat('en-US', { year: 'numeric', month: '2-digit', day: '2-digit' }).format(d);
};

const isoOrNull = (d: any) => {
  if (!d) return null;
  if (typeof d === 'string' && /^\d{4}-\d{2}-\d{2}$/.test(d)) return `${d}T00:00:00.000Z`;
  if (d instanceof Date) return d.toISOString();
  return null;
};

const cleanNulls = <T extends Record<string, any>>(obj: T): Partial<T> => {
  const out: any = {};
  for (const [k, v] of Object.entries(obj)) {
    if (v === null || v === undefined || v === '') continue;
    out[k] = v;
  }
  return out;
};

const getOptId = (opt: any): string | null => {
  const v = opt?.value ?? opt?.id ?? opt?.ID ?? opt?.key ?? null;
  return v ? String(v) : null;
};

const GlAccountStatement_Paged: React.FC = () => {
  const { t, i18n } = useTranslation();
  const isAr = i18n.language !== 'en';

  const dispatch = useAppDispatch();

  const { OptionsAccounts, OptionsBranches, OptionsCostCenters } = useGlChartLookups();

  // ===== Options Hooks
  const branchHook = useGetOperatingUnitWithPermission() as any;
  const loadingBranches = branchHook?.loading ?? false;

  // Placeholder for Accounts Options (استبدلها بالداتا الحقيقية من الهوك الخاص بك)
  const loadingAccounts = false;

  // Placeholder for Cost Centers Options (استبدلها بالداتا الحقيقية من الهوك الخاص بك)
  const loadingCostCenters = false;

  // ===== Filters State
  const [accountFilter, setAccountFilter] = useState<any>(null);
  const [branchFilter, setBranchFilter] = useState<any>(null);
  const [costCenterFilter, setCostCenterFilter] = useState<any>(null);
  const [fromDate, setFromDate] = useState<any>(null);
  const [toDate, setToDate] = useState<any>(null);

  const [showExport, setShowExport] = useState(false);

  // ===== Report state
  const report = useAppSelector(s => selectReport(s as any, REPORT_KEY));
  const items = (report?.items ?? []) as RowDto[];
  const meta = report?.meta ?? null;
  const loading = report?.loading ?? false;
  const error = report?.error ?? null;

  useEffect(() => {
    const initialRequest: GlAccountStatementReq = {
      pageIndex: 0,
      pageSize: 50
    };

    dispatch(
      initReport({
        reportKey: REPORT_KEY,
        endpoint: ENDPOINT,
        initialRequest
      })
    );
  }, [dispatch]);

  const applyFilters = () => {
    const prevReq = (((report as any)?.request ?? {}) as GlAccountStatementReq) || {};

    const nextReq: GlAccountStatementReq = {
      ...prevReq,
      pageIndex: 0,
      pageSize: prevReq.pageSize ?? 50,
      froM_DATE: isoOrNull(fromDate),
      tO_DATE: isoOrNull(toDate),
      accounT_ID: getOptId(accountFilter),
      brancH_ID: getOptId(branchFilter),
      cosT_CENTER_ID: getOptId(costCenterFilter)
    };

    dispatch(setReportRequest({ reportKey: REPORT_KEY, request: cleanNulls(nextReq) as any }));
    dispatch(getReportPagedData({ reportKey: REPORT_KEY }));
  };

  const clearFilters = () => {
    setAccountFilter(null);
    setBranchFilter(null);
    setCostCenterFilter(null);
    setFromDate(null);
    setToDate(null);

    const resetReq: GlAccountStatementReq = {
      pageIndex: 0,
      pageSize: 50
    };

    dispatch(setReportRequest({ reportKey: REPORT_KEY, request: resetReq as any }));
  };

  // Totals Cards
  const TotalsCards = () => {
    if (!meta) return null;

    return (
      <div className='row g-3 mb-4'>
        {/* رصيد افتتاحي */}
        <div className='col-lg-3 col-md-6'>
          <Card className='bg-primary bg-opacity-10 border-0 shadow-sm h-100'>
            <div className='card-body d-flex align-items-center'>
              <div className='p-3 me-3'>
                <i className='fas fa-scale-balanced text-primary fa-xl'></i>
              </div>
              <div>
                <h6 className='text-primary fw-bold mb-1'>{isAr ? 'الرصيد الافتتاحي' : 'Opening Balance'}</h6>
                <h4 className='mb-0 fw-bold' dir='ltr'>
                  {formatNumber(meta.openingBalance)}
                </h4>
              </div>
            </div>
          </Card>
        </div>

        {/* إجمالي مدين */}
        <div className='col-lg-3 col-md-6'>
          <Card className='bg-success bg-opacity-10 border-0 shadow-sm h-100'>
            <div className='card-body d-flex align-items-center'>
              <div className='p-3 me-3'>
                <i className='fas fa-arrow-down text-success fa-xl'></i>
              </div>
              <div>
                <h6 className='text-success fw-bold mb-1'>{isAr ? 'إجمالي مدين' : 'Total Debit (DR)'}</h6>
                <h4 className='mb-0 fw-bold'>{formatNumber(meta.total_DR)}</h4>
              </div>
            </div>
          </Card>
        </div>

        {/* إجمالي دائن */}
        <div className='col-lg-3 col-md-6'>
          <Card className='bg-danger bg-opacity-10 border-0 shadow-sm h-100'>
            <div className='card-body d-flex align-items-center'>
              <div className='p-3 me-3'>
                <i className='fas fa-arrow-up text-danger fa-xl'></i>
              </div>
              <div>
                <h6 className='text-danger fw-bold mb-1'>{isAr ? 'إجمالي دائن' : 'Total Credit (CR)'}</h6>
                <h4 className='mb-0 fw-bold'>{formatNumber(meta.total_CR)}</h4>
              </div>
            </div>
          </Card>
        </div>

        {/* رصيد ختامي */}
        <div className='col-lg-3 col-md-6'>
          <Card className='bg-warning bg-opacity-10 border-0 shadow-sm h-100'>
            <div className='card-body d-flex align-items-center'>
              <div className='p-3 me-3'>
                <i className='fas fa-calculator text-warning fa-xl'></i>
              </div>
              <div>
                <h6 className='text-warning fw-bold mb-1'>{isAr ? 'الرصيد الختامي' : 'Closing Balance'}</h6>
                <h4 className='mb-0 fw-bold' dir='ltr'>
                  {formatNumber(meta.closingBalance)}
                </h4>
              </div>
            </div>
          </Card>
        </div>
      </div>
    );
  };

  const exportColumns: ExportColumn<RowDto>[] = [
    { headerAr: 'التاريخ', headerEn: 'GL Date', value: r => formatDate(r.gL_DATE) },
    { headerAr: 'رقم الحساب', headerEn: 'Account Number', value: r => r.accounT_NUMBER || '' },
    { headerAr: 'اسم الحساب', headerEn: 'Account Name', value: r => r.accounT_NAME || '' },
    { headerAr: 'الفرع', headerEn: 'Branch', value: r => r.brancH_NAME || '' },
    { headerAr: 'مركز التكلفة', headerEn: 'Cost Center', value: r => r.cosT_CENTER_NAME || '' },
    { headerAr: 'بيان القيد (رئيسي)', headerEn: 'Header Description', value: r => r.headeR_NAME || '' },
    { headerAr: 'بيان السطر (فرعي)', headerEn: 'Line Description', value: r => r.jE_LINE_DESCRIPTION || '' },
    { headerAr: 'مدين', headerEn: 'Debit', value: r => r.amounT_DR || 0 },
    { headerAr: 'دائن', headerEn: 'Credit', value: r => r.amounT_CR || 0 },
    { headerAr: 'الرصيد المتراكم', headerEn: 'Cumulative Balance', value: r => r.cumulativE_BALANCE || 0 }
  ];

  return (
    <>
      <CardHeader className='mb-3'>
        <div className='row g-2 align-items-center mb-2'>
          {/* Account */}
          <div className='col-lg-4'>
            <div className='input-group'>
              <span className='input-group-text fw-bold' title={isAr ? 'الحساب' : 'Account'}>
                <i className='fas fa-file-invoice-dollar text-primary'></i>
              </span>
              <Select
                isLoading={loadingAccounts}
                value={accountFilter}
                onChange={setAccountFilter}
                options={OptionsAccounts}
                placeholder={isAr ? 'الحساب' : 'Account'}
                isClearable
                isSearchable
                className='flex-grow-1'
                classNamePrefix='react-select'
                menuPortalTarget={document.body}
                styles={{ menuPortal: (base: any) => ({ ...base, zIndex: 9999 }) }}
              />
            </div>
          </div>

          {/* Branch */}
          <div className='col-lg-4'>
            <div className='input-group'>
              <span className='input-group-text fw-bold' title={isAr ? 'الفرع' : 'Branch'}>
                <i className='fas fa-store text-info'></i>
              </span>
              <Select
                isLoading={loadingBranches}
                value={branchFilter}
                onChange={setBranchFilter}
                options={OptionsBranches}
                placeholder={isAr ? 'الفرع' : 'Branch'}
                isClearable
                isSearchable
                className='flex-grow-1'
                classNamePrefix='react-select'
                menuPortalTarget={document.body}
                styles={{ menuPortal: (base: any) => ({ ...base, zIndex: 9999 }) }}
              />
            </div>
          </div>

          {/* Cost Center */}
          <div className='col-lg-4'>
            <div className='input-group'>
              <span className='input-group-text fw-bold' title={isAr ? 'مركز التكلفة' : 'Cost Center'}>
                <i className='fas fa-bullseye text-warning'></i>
              </span>
              <Select
                isLoading={loadingCostCenters}
                value={costCenterFilter}
                onChange={setCostCenterFilter}
                options={OptionsCostCenters}
                placeholder={isAr ? 'مركز التكلفة' : 'Cost Center'}
                isClearable
                isSearchable
                className='flex-grow-1'
                classNamePrefix='react-select'
                menuPortalTarget={document.body}
                styles={{ menuPortal: (base: any) => ({ ...base, zIndex: 9999 }) }}
              />
            </div>
          </div>
        </div>

        <div className='row g-2 align-items-center'>
          {/* From Date */}
          <div className='col-lg-3'>
            <div className='input-group'>
              <span className='input-group-text fw-bold'>
                <i className='fas fa-calendar'></i>
              </span>
              <input
                type='date'
                className='form-control'
                value={fromDate || ''}
                onChange={e => setFromDate(e.target.value)}
                placeholder={isAr ? 'من تاريخ' : 'From Date'}
              />
            </div>
          </div>

          {/* To Date */}
          <div className='col-lg-3'>
            <div className='input-group'>
              <span className='input-group-text fw-bold'>
                <i className='fas fa-calendar'></i>
              </span>
              <input
                type='date'
                className='form-control'
                value={toDate || ''}
                onChange={e => setToDate(e.target.value)}
                min={fromDate || undefined}
                placeholder={isAr ? 'إلى تاريخ' : 'To Date'}
              />
            </div>
          </div>

          {/* Buttons */}
          <div className='col-lg-6 text-end'>
            <button
              className='btn btn-outline-secondary me-2'
              type='button'
              onClick={clearFilters}
              title={t('ClearFilters')}
            >
              <i className='fas fa-times fa-fw'></i>
            </button>
            <button className='btn btn-theme  me-2' type='button' onClick={applyFilters} title={t('ApplyFilters')}>
              <i className='fas fa-filter fa-fw'></i>
            </button>
            <button
              className='btn btn-outline-success'
              type='button'
              onClick={() => setShowExport(true)}
              title={t('Export')}
              disabled={loading || items.length === 0}
            >
              <i className='fas fa-file-excel fa-fw'></i>
            </button>   
          </div>
        </div>
      </CardHeader>

      <TotalsCards />

      <Card className='mb-4'>
        <Loading loading={loading} error={error}>
          <div className='table-responsive' style={{ maxHeight: 'calc(100vh - 320px)', overflowY: 'auto' }}>
            <table className='table table-hover mb-0'>
              <thead className='sticky-top' style={{ top: 0, zIndex: 10 }}>
                <tr className='bg-theme bg-opacity-35' style={{ backdropFilter: 'blur(20px)' }}>
                  <th className='small' style={{ minWidth: '110px' }}>
                    {isAr ? 'التاريخ' : 'Date'}
                  </th>
                  <th className='small' style={{ minWidth: '180px' }}>
                    {isAr ? 'الحساب' : 'Account'}
                  </th>
                  <th className='small' style={{ minWidth: '140px' }}>
                    {isAr ? 'الفرع' : 'Branch'}
                  </th>
                  <th className='small' style={{ minWidth: '160px' }}>
                    {isAr ? 'مركز التكلفة' : 'Cost Center'}
                  </th>
                  <th className='small' style={{ minWidth: '220px' }}>
                    {isAr ? 'بيان القيد الرئيسي' : 'Header Desc.'}
                  </th>
                  <th className='small' style={{ minWidth: '240px' }}>
                    {isAr ? 'بيان السطر' : 'Line Desc.'}
                  </th>
                  <th className='small text-end' style={{ minWidth: '120px' }}>
                    {isAr ? 'مدين' : 'Debit'}
                  </th>
                  <th className='small text-end' style={{ minWidth: '120px' }}>
                    {isAr ? 'دائن' : 'Credit'}
                  </th>
                  <th className='small text-end' style={{ minWidth: '140px' }}>
                    {isAr ? 'الرصيد المتراكم' : 'Running Balance'}
                  </th>
                </tr>
              </thead>

              <tbody>
                {loading ? null : items.length === 0 ? (
                  <tr>
                    <td colSpan={9} className='text-center py-5'>
                      <div className='text-muted'>
                        {accountFilter ? (
                          <>
                            <i className='fas fa-file-invoice fa-3x mb-3 opacity-50'></i>
                            <h6>{t('NoData')}</h6>
                          </>
                        ) : (
                          <>
                            <i className='fas fa-filter fa-3x mb-3 opacity-50 text-warning'></i>
                            <h6 className='text-warning'>
                              {isAr
                                ? 'يرجى اختيار الحساب والضغط على تطبيق'
                                : 'Please select an account and apply filter'}
                            </h6>
                          </>
                        )}
                      </div>
                    </td>
                  </tr>
                ) : (
                  items.map((row, index) => (
                    <tr key={index} className='align-middle'>
                      {/* التاريخ */}
                      <td>
                        <div className='fw-medium'>{formatDate(row.gL_DATE)}</div>
                      </td>

                      {/* الحساب – نفس الطريقة المفضلة عندك */}
                      <td>
                        <div className='fw-bold small'>{safe(row.accounT_NAME)}</div>
                        <div className='text-muted tiny'>{safe(row.accounT_NUMBER)}</div>
                      </td>

                      {/* الفرع – بستايل البادج الخفيف */}
                      <td>
                        {row.brancH_NAME ? (
                          <span className=' '>{row.brancH_NAME}</span>
                        ) : (
                          <span className='text-muted opacity-50'>—</span>
                        )}
                      </td>

                      {/* مركز التكلفة – بادج بلون مختلف */}
                      <td>
                        {row.cosT_CENTER_NAME ? (
                          <span className='badge bg-warning bg-opacity-60 border border-warning-subtle text-dark'>
                            {row.cosT_CENTER_NAME}
                          </span>
                        ) : (
                          <span className='text-muted opacity-50'>—</span>
                        )}
                      </td>

                      {/* بيان القيد الرئيسي */}
                      <td>
                        <div className='small text-muted'>{safe(row.headeR_NAME, '—')}</div>
                      </td>

                      {/* بيان السطر */}
                      <td>
                        <div className='small text-muted'>{safe(row.jE_LINE_DESCRIPTION, '—')}</div>
                      </td>

                      {/* مدين */}
                      <td className='text-end'>
                        {row.amounT_DR > 0 ? (
                          <div className='fw-bold text-success'>{formatNumber(row.amounT_DR)}</div>
                        ) : (
                          <div className='text-muted opacity-50'>0.00</div>
                        )}
                      </td>

                      {/* دائن */}
                      <td className='text-end'>
                        {row.amounT_CR > 0 ? (
                          <div className='fw-bold text-danger'>{formatNumber(row.amounT_CR)}</div>
                        ) : (
                          <div className='text-muted opacity-50'>0.00</div>
                        )}
                      </td>

                      {/* الرصيد المتراكم – مع التمييز الخلفي واللون */}
                      <td className='text-end bg-secondary bg-opacity-10'>
                        <div
                          className={`fw-bold fs-6 ${
                            row.cumulativE_BALANCE < 0
                              ? 'text-danger'
                              : row.cumulativE_BALANCE > 0
                                ? 'text-success'
                                : 'text-dark'
                          }`}
                        >
                          {formatNumber(row.cumulativE_BALANCE)}
                        </div>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        </Loading>

        <CardFooter>
          <div className='d-flex justify-content-between align-items-center flex-wrap gap-3'>
            <ReportPagination reportKey={REPORT_KEY} maxVisiblePages={3} showItemsPerPage={true} />
          </div>
        </CardFooter>
      </Card>

      <ReportExportExcelModal
        show={showExport}
        onHide={() => setShowExport(false)}
        reportKey={REPORT_KEY}
        endpoint={ENDPOINT}
        columns={exportColumns}
        exportPageSize={4000}
        sheetName={({ isAr }) => (isAr ? 'كشف حساب' : 'Account Statement')}
        fileName={({ reportKey }) => `${reportKey}_${new Date().toISOString().slice(0, 10)}.xlsx`}
      />
    </>
  );
};

export default GlAccountStatement_Paged;
